import { useEffect, useMemo, useRef, useState } from 'react';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { LayerSpecification, Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { getMapboxToken, type UMMapHandle } from '../../urbanmap/_components';
import { ProjectMap } from './ProjectMap';
import { CUSTOMERS, NODES, ROADS, roadCoordinates, uid } from './data';
import { effectiveRule } from './mocks';
import type { Departure, DevelopmentPlan, Hypotheses, MapTool, NetworkKind, Pipe, Position, Road } from './types';
import { useDraftGuard } from './DirtyContext';

function collection(features: Feature<Geometry>[]): FeatureCollection { return { type: 'FeatureCollection', features }; }
function line(id: string, coordinates: Position[], properties: Record<string, string | number> = {}): Feature {
  return { type: 'Feature', id, properties: { id, ...properties }, geometry: { type: 'LineString', coordinates } };
}
function nearest(map: MapboxMap, event: MapMouseEvent, roads: Road[]) {
  let best: { road: Road; position: Position; distance: number } | undefined;
  roads.forEach(road => {
    const [start, end] = roadCoordinates(road);
    const a = map.project(start), b = map.project(end), p = event.point;
    const dx = b.x - a.x, dy = b.y - a.y;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)));
    const distance = Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
    if (!best || distance < best.distance) best = {
      road, distance, position: [start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t],
    };
  });
  return best;
}
export function NetworkMap({ hypotheses, plan, selectedRoad, onSelect, tool, networkKind, onDeparture, onManual, onFinish }: {
  hypotheses: Hypotheses; plan?: DevelopmentPlan; selectedRoad: string; onSelect: (id: string) => void;
  tool: MapTool; networkKind: NetworkKind; onDeparture: (departure: Departure) => void;
  onManual: (pipe: Pipe) => void; onFinish: () => void;
}) {
  const mapRef = useRef<UMMapHandle>(null);
  const [points, setPoints] = useState<Position[]>([]);
  useDraftGuard(points.length > 0);
  const [message, setMessage] = useState('');
  const [offline, setOffline] = useState(true);
  const latest = useRef({ tool, points, onDeparture, onManual, onFinish, networkKind });
  latest.current = { tool, points, onDeparture, onManual, onFinish, networkKind };
  useEffect(() => { setPoints([]); setMessage(''); }, [tool]);
  useEffect(() => {
    if (!getMapboxToken()) return;
    let frame = 0;
    let attached: MapboxMap | null = null;
    const click = (e: MapMouseEvent) => {
      const current = latest.current;
      if (current.tool === 'inspect' || !attached) return;
      if (current.tool === 'manual') {
        setPoints(p => [...p, [e.lngLat.lng, e.lngLat.lat]]);
        return;
      }
      if (current.tool === 'plant' && current.points.length === 0) {
        setPoints([[e.lngLat.lng, e.lngLat.lat]]);
        setMessage('Centrale placée. Cliquez sur une route pour dessiner sa liaison.');
        return;
      }
      const snapped = nearest(attached, e, ROADS.filter(r => current.tool !== 'network' || r.existing));
      if (!snapped || snapped.distance > 18) {
        setMessage('Cliquez plus près d’un tronçon pour l’accrocher par snapping.');
        return;
      }
      if (current.tool === 'network' && current.networkKind === 'new') {
        setMessage('Un réseau nouveau accepte uniquement des centrales.'); return;
      }
      current.onDeparture({
        id: uid(), label: current.tool === 'plant' ? 'Nouvelle centrale' : snapped.road.name,
        kind: current.tool === 'plant' ? 'plant' : 'network', roadId: snapped.road.id,
        coordinates: current.tool === 'plant' ? [current.points[0], snapped.position] : roadCoordinates(snapped.road),
      });
      setPoints([]); setMessage('Départ ajouté au scénario.'); current.onFinish();
    };
    const attach = () => {
      const map = mapRef.current?.getMap();
      if (!map) { frame = requestAnimationFrame(attach); return; }
      attached = map; map.on('click', click);
    };
    frame = requestAnimationFrame(attach);
    return () => { cancelAnimationFrame(frame); attached?.off('click', click); };
  }, []);
  const sources = useMemo(() => ({
    roads: collection(ROADS.map(r => line(r.id, roadCoordinates(r), {
      mode: effectiveRule(hypotheses, r).mode, weight: effectiveRule(hypotheses, r).weight,
    }))),
    existing: collection(networkKind === 'existing' ? ROADS.filter(r => r.existing).map(r => line(r.id, roadCoordinates(r))) : []),
    selected: collection(ROADS.filter(r => r.id === selectedRoad).map(r => line(r.id, roadCoordinates(r)))),
    departures: collection(hypotheses.departures.filter(d => networkKind === 'existing' || d.kind === 'plant')
      .map(d => line(d.id, d.coordinates))),
    plants: collection(hypotheses.departures.filter(d => d.kind === 'plant').map(d => ({
      type: 'Feature', properties: { id: d.id }, geometry: { type: 'Point', coordinates: d.coordinates[0] },
    }))),
    customers: collection(CUSTOMERS.map(c => ({
      type: 'Feature', properties: { id: c.id, selected: plan && c.id in plan.subscribers ? 1 : 0 },
      geometry: { type: 'Point', coordinates: NODES[c.node] },
    }))),
    pipes: collection((plan?.pipes ?? []).map(p => line(p.id, p.coordinates, { manual: p.manual ? 1 : 0 }))),
    drawing: collection(points.length >= 2 ? [line('draft', points)] : []),
    draftPoint: collection(points.map((p, i) => ({
      type: 'Feature', properties: { id: `draft-${i}` }, geometry: { type: 'Point', coordinates: p },
    }))),
  }), [hypotheses, networkKind, selectedRoad, plan, points]);
  const layers = useMemo<LayerSpecification[]>(() => [
    { id: 'road-halo', type: 'line', source: 'roads', paint: { 'line-color': '#ffffff', 'line-width': 16 } },
    { id: 'roads', type: 'line', source: 'roads', paint: {
      'line-color': ['match', ['get', 'mode'], 'forbidden', '#c44234', 'forced', '#00726d', '#778c9b'],
      'line-width': ['match', ['get', 'mode'], 'forced', 7, 4],
    } },
    { id: 'selected', type: 'line', source: 'selected', paint: { 'line-color': '#007bc5', 'line-width': 10, 'line-opacity': 0.35 } },
    { id: 'existing', type: 'line', source: 'existing', paint: { 'line-color': '#009de9', 'line-width': 4, 'line-dasharray': [2, 1] } },
    { id: 'departures', type: 'line', source: 'departures', paint: { 'line-color': '#238657', 'line-width': 7, 'line-opacity': 0.8 } },
    { id: 'pipes', type: 'line', source: 'pipes', paint: {
      'line-color': ['case', ['==', ['get', 'manual'], 1], '#bc5921', plan?.energy === 'cold' ? '#007bc5' : '#7a3fd1'], 'line-width': 6,
    } },
    { id: 'customers', type: 'circle', source: 'customers', paint: {
      'circle-radius': 9, 'circle-color': ['case', ['==', ['get', 'selected'], 1], '#7a3fd1', '#ffffff'],
      'circle-stroke-width': 2, 'circle-stroke-color': '#435c6e',
    } },
    { id: 'plants', type: 'circle', source: 'plants', paint: {
      'circle-radius': 10, 'circle-color': '#238657', 'circle-stroke-width': 3, 'circle-stroke-color': '#ffffff',
    } },
    { id: 'drawing', type: 'line', source: 'drawing', paint: { 'line-color': '#bc5921', 'line-width': 5, 'line-dasharray': [2, 1] } },
    { id: 'draft-point', type: 'circle', source: 'draftPoint', paint: { 'circle-radius': 6, 'circle-color': '#bc5921' } },
  ], [plan?.energy]);
  const finishManual = () => {
    onManual({ id: uid(), name: 'Canalisation manuelle', manual: true, coordinates: points, month: '' });
    setPoints([]); onFinish();
  };
  return <div className="sp-map">
    <ProjectMap ref={mapRef} sources={sources} layers={layers}
      initialBounds={[[2.335, 48.804], [2.353, 48.816]]}
      interactiveLayerIds={['roads']} onFeatureClick={(_, feature) => {
        if (tool === 'inspect' && typeof feature.properties?.id === 'string') onSelect(feature.properties.id);
      }} onModeChange={mode => setOffline(mode === 'offline')} />
    <div className="sp-map-title">
      <NJText as="div"><strong>Gentilly</strong> · {offline ? 'Données locales de démonstration' : 'Fond cartographique'}</NJText>
      <NJText as="div" scale="sm">Graphe de démonstration · géométries non contractuelles</NJText>
    </div>
    <div className="sp-map-tools">
      <NJButton label="Zoom +" scale="sm" emphasis="subtle" disabled={!getMapboxToken()} onClick={() => mapRef.current?.zoomIn()} />
      <NJButton label="Zoom −" scale="sm" emphasis="subtle" disabled={!getMapboxToken()} onClick={() => mapRef.current?.zoomOut()} />
    </div>
    {tool !== 'inspect' && <div className="sp-draw-help" role="status">
      <NJText as="div">{message || ({
        network: 'Cliquez sur un tronçon bleu du réseau existant.',
        plant: 'Placez la centrale, puis cliquez sur une route pour la relier.',
        manual: 'Cliquez pour ajouter les sommets de la canalisation, puis terminez.',
      }[tool])}</NJText>
      <div className="sp-row">
        {tool === 'manual' && <NJButton label="Terminer le dessin" scale="sm" disabled={points.length < 2} onClick={finishManual} />}
        <NJButton label="Annuler le dessin" scale="sm" emphasis="minimal" onClick={onFinish} />
      </div>
    </div>}
    <div className="sp-legend">
      <NJText as="div" scale="sm"><strong>Légende</strong> · routes grises · interdits rouges · forcés verts</NJText>
      <NJText as="div" scale="sm">Réseau existant bleu pointillé · départs verts · abonnés cerclés</NJText>
      <NJText as="div" scale="sm">Plan violet (chaleur) / bleu (froid) · dessin manuel orange</NJText>
    </div>
  </div>;
}
